import { create } from "zustand";

export interface AlertState {
  id: string;
  title?: string;
  description: string;
  variant?: "default" | "destructive";
  duration?: number;
}

interface AlertStore {
  currentAlert: AlertState | null;
  alertQueue: AlertState[];
  isOpen: boolean;
  showAlert: (alert: Omit<AlertState, "id">) => void;
  hideAlert: () => void;
  clearAllAlerts: () => void;
  processNextAlert: () => void;
}

export const useAlertStore = create<AlertStore>((set, get) => ({
  currentAlert: null,
  alertQueue: [],
  isOpen: false,

  showAlert: (alert: Omit<AlertState, "id">) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newAlert: AlertState = {
      ...alert,
      id,
    };

    const { currentAlert, isOpen } = get();

    if (currentAlert || isOpen) {
      // If there's already an alert showing, add to queue
      set((state) => ({
        alertQueue: [...state.alertQueue, newAlert],
      }));
    } else {
      // Show immediately if no alert is currently displayed
      set({
        currentAlert: newAlert,
        isOpen: true,
      });
    }
  },

  hideAlert: () => {
    set((state) => {
      const nextAlert = state.alertQueue[0];
      const remainingQueue = state.alertQueue.slice(1);

      if (nextAlert) {
        // Show next alert from queue
        return {
          currentAlert: nextAlert,
          alertQueue: remainingQueue,
          isOpen: true,
        };
      } else {
        // No more alerts to show
        return {
          currentAlert: null,
          alertQueue: remainingQueue,
          isOpen: false,
        };
      }
    });
  },

  clearAllAlerts: () => {
    set({
      currentAlert: null,
      alertQueue: [],
      isOpen: false,
    });
  },

  processNextAlert: () => {
    const { alertQueue } = get();
    if (alertQueue.length > 0) {
      const nextAlert = alertQueue[0];
      set((state) => ({
        currentAlert: nextAlert,
        alertQueue: state.alertQueue.slice(1),
        isOpen: true,
      }));
    }
  },
}));

// Convenience hook for easier usage
export const useAlert = () => {
  const store = useAlertStore();
  return {
    currentAlert: store.currentAlert,
    isOpen: store.isOpen,
    showAlert: store.showAlert,
    hideAlert: store.hideAlert,
    clearAllAlerts: store.clearAllAlerts,
  };
};
